import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle2, Pencil, RotateCcw, Send } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { FEATURE_ESCALATIONS } from '@/lib/features';
import { STAGE_LABELS, type PausedStage } from '@/types/enums';
import { STAGE_SPECS } from './stageData';
import { ManualIngestDialog } from './ManualIngestDialog';
import { EscalateButton } from './EscalateButton';
import { DocAssistantFallbackNotice, StageOutputSections } from './StageReviewOutput';
import {
  EditField,
  coerce,
  fieldFrom,
  stringifyEditValue,
} from './StageReviewBits';

interface StageRow {
  id: string;
  output_json: Record<string, unknown> | null;
  edits_json: Record<string, unknown> | null;
  review_state: 'awaiting_review' | 'approved' | 'edited_approved';
  error: string | null;
  completed_at: string | null;
}

interface Props {
  jobId: string;
  stage: PausedStage;
  /** Parent re-fetches the job after approve so review_status flips. */
  onApproved: () => void;
  /** Parent bumps the activity-feed refresh key whenever a view-only
   *  action (escalate, translate, …) succeeds on this stage. */
  onActivity?: () => void;
}

export function StageReviewPanel({ jobId, stage, onApproved, onActivity }: Props) {
  const spec = STAGE_SPECS[stage];
  const [row, setRow] = useState<StageRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ingestOpen, setIngestOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api
      .get<StageRow>(`/api/v1/jobs/${jobId}/stages/${stage}`)
      .then((r) => {
        setRow(r);
        const current = fieldFrom(r.output_json, spec.edit.key);
        setEditValue(stringifyEditValue(current));
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof ApiError ? err.message : 'Failed to load stage data.';
        setLoadError(msg);
      })
      .finally(() => { setLoading(false); });
  }, [jobId, stage, spec.edit.key]);

  useEffect(() => { load(); }, [load]);

  async function handleRetry() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post(`/api/v1/jobs/${jobId}/stages/${stage}/retry`, {});
      // The backend will write a new awaiting_review row; the parent's
      // polling will pick it up. Trigger an immediate reload here too.
      load();
      onApproved();
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError ? err.message : 'Retry failed. Please retry.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: Record<string, unknown> = {};
      if (editMode) {
        body.edited_output = { [spec.edit.key]: coerce(editValue, spec.edit.kind) };
      }
      await api.post(`/api/v1/jobs/${jobId}/stages/${stage}/approve`, body);
      onApproved();
    } catch (err: unknown) {
      // 409 from approve means "already paused at a different stage" — the
      // pipeline has already advanced. That's not an error from the
      // reviewer's perspective; just refresh and move on.
      if (err instanceof ApiError && err.status === 409) {
        onApproved();
        return;
      }
      const msg =
        err instanceof ApiError ? err.message : 'Approval failed. Please retry.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  const output = row?.output_json ?? {};
  const summaryKeys = spec.summaryKeys ?? [];
  // The Document Assistant returned no usable transcription. Drives both the
  // fallback notice and the Approve button's label.
  const docAssistantEmpty =
    stage === 'document_assistant' &&
    output.extraction_complete === false &&
    typeof output.text === 'string' &&
    output.text.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <span>{STAGE_LABELS[stage]} agent — awaiting human approval</span>
        </CardTitle>
        <p className="text-sm text-muted-foreground">{spec.blurb}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {row?.error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription className="space-y-2">
              <div>
                <span className="font-medium">Agent reported an error:</span>{' '}
                {row.error}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="bg-background"
                  onClick={() => { setIngestOpen(true); }}
                >
                  Paste output from Kaycoo
                </Button>
                <span className="text-xs text-muted-foreground">
                  Use this when the agent succeeded on the Kaycoo dashboard but the
                  response never reached CMP.
                </span>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <StageOutputSections
          stage={stage}
          output={output}
          jobId={jobId}
          summaryKeys={summaryKeys}
          onActivity={onActivity}
        />

        {editMode && (
          <EditField
            field={spec.edit}
            value={editValue}
            onChange={setEditValue}
          />
        )}

        {submitError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        {docAssistantEmpty && <DocAssistantFallbackNotice />}

        <div className="flex items-center gap-2 pt-1">
          <Button
            variant={editMode ? 'outline' : 'default'}
            onClick={() => { setEditMode((m) => !m); }}
            disabled={submitting}
          >
            <Pencil className="h-4 w-4 mr-1.5" aria-hidden="true" />
            {editMode ? 'Cancel edit' : 'Edit before approve'}
          </Button>
          <Button
            variant="outline"
            onClick={() => { void handleRetry(); }}
            disabled={submitting}
            className="gap-1.5"
            title="Re-run this agent (useful when the agent errored)"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Retry agent
          </Button>
          {FEATURE_ESCALATIONS && (
            <EscalateButton
              jobId={jobId}
              stage={stage}
              agentSummary={
                typeof output.narrative === 'string'
                  ? output.narrative
                  : typeof output.summary === 'string'
                    ? output.summary
                    : undefined
              }
              disabled={submitting}
              onEscalated={onActivity}
            />
          )}
          <Button onClick={() => { void handleApprove(); }} disabled={submitting} className="gap-1.5">
            {editMode ? (
              <>
                <Send className="h-4 w-4" aria-hidden="true" />
                Save edit & approve
              </>
            ) : docAssistantEmpty ? (
              <>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Skip &amp; use fallback OCR
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Approve and run next agent
              </>
            )}
          </Button>
        </div>
      </CardContent>
      <ManualIngestDialog
        jobId={jobId}
        stage={stage}
        open={ingestOpen}
        onClose={() => { setIngestOpen(false); }}
        onIngested={load}
      />
    </Card>
  );
}

